import React, { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ReactComponent as AddCircleIcon } from '../../assets/images/add-circle.svg'
import { ReactComponent as CheckCircleIcon } from '../../assets/images/check-circle-1.svg'
import { ReactComponent as DenyIcon } from '../../assets/images/Close.svg'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
    Button,
    Typography,
    Chip,
    Tooltip,
    CircularProgress,
    IconButton,
    Box,
    Popover,
    SvgIcon,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { ReactComponent as ActionsIcon } from '../../assets/images/actions/actions.svg'
import { useSelector } from 'react-redux'
import { useAuth0 } from '../../contexts/auth0-context'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { PUBLICATIONS } from '../../shared/constants/publications'
import store from '../../redux/store'
import { useGetDraftKES, KES } from '../../shared/queries/useGetDraftKES'
import { useProcessEvidenceForMaiaMutation } from '../../shared/queries/useProcessEvidenceForMaiaMutation'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'

interface Props {
    draft: any
    open: boolean
    onClose: () => void
    onSaved: (updatedDraft: any) => void
    onDeleted: (id: string) => void
}

const STATUS_DISPLAY = {
    draft: 'Draft',
    'in review': 'In Review',
    rejected: 'Rejected',
    accepted: 'Accepted',
}

const STATUS_BADGE_COLOR = {
    draft: 'figma.grayscale.tier4',
    'in review': 'figma.accentColors.orange',
    rejected: 'figma.accentColors.red',
    accepted: 'figma.accentColors.green',
}

const STATUS_ACTIONS = [
    { label: 'Accept', value: 'accepted' },
    { label: 'Mark as In Review', value: 'in review' },
    { label: 'Mark as Draft', value: 'draft' },
    { label: 'Rejected', value: 'rejected' },
]

function initEdits(draft: any) {
    return {
        name: draft.name || '',
        abstract: draft.abstract || '',
        year: draft.year || '',
        category: draft.category || '',
        internal: draft.internal || false,
        population: draft.population || '---',
        publicationType: draft.publicationType || '---',
        uniqueStudyIdentifier: draft.uniqueStudyIdentifier || '',
        nickname: draft.nickname || '',
        citation: draft.citation || '',
        shortCitation: draft.shortCitation || '',
        longCitation: draft.longCitation || '',
        author: draft.author || '',
        pubmedId: draft.pubmedId || '',
        pmcId: draft.pmcId || '',
        link: draft.link || '',
        status: draft.status || 'draft',
    }
}

export default function DraftEditDialog({ draft, open, onClose, onSaved, onDeleted }: Props) {
    const { getTokenSilently } = useAuth0()
    const settings = useSelector((state: any) => state.settings)
    const evidenceCategories: string[] = settings?.evidenceCategories || []

    const [edits, setEdits] = useState(initEdits(draft))
    const { mutate: processEvidenceForMaia } = useProcessEvidenceForMaiaMutation()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()
    const [saving, setSaving] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null)
    const [localKES, setLocalKES] = useState<Array<{ id: string; _id?: string; statement: string; figures: string[] }>>(
        []
    )

    // `id` is a frontend-only stable key used for React rendering and edit/delete targeting.
    // It is never sent to the server. `_id` (set when loading existing KES) is the real MongoDB ID.
    const handleAddKES = () => {
        setLocalKES((prev) => [...prev, { id: uuidv4(), statement: '', figures: [] }])
    }

    const handleKESTextChange = (id: string, text: string) => {
        setLocalKES((prev) => prev.map((k) => (k.id === id ? { ...k, statement: text } : k)))
    }

    const handleKESFileSelect = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const files = Array.from(e.target.files || []).filter((f) => {
            const ext = f.name.split('.').pop()?.toLowerCase()
            return ext === 'png' || ext === 'jpg' || ext === 'jpeg'
        })
        const readers = files.map(
            (f) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(f)
                    reader.onload = () => resolve(reader.result as string)
                    reader.onerror = reject
                })
        )
        Promise.all(readers).then((base64Array) => {
            setLocalKES((prev) =>
                prev.map((k) => (k.id === id ? { ...k, figures: [...k.figures, ...base64Array] } : k))
            )
        })
    }

    const saveLocalKES = async (draftId: string) => {
        if (!localKES.length) return
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        await callAPI(endpoints.handleKeyEvidenceStatements.url, endpoints.handleKeyEvidenceStatements.method, token, {
            statements: localKES.map((k) => ({
                ...(k._id ? { _id: k._id } : {}),
                statement: k.statement,
                figures: k.figures,
                status: 'approved',
                parentDraft: draftId,
                strategyId: store.getState().strategy._id,
                organizationId: store.getState().strategy.organizationId,
            })),
            deleteIds: [],
        })
        setLocalKES([])
    }
    const [pendingKES, setPendingKES] = useState<KES[]>([])
    const [pendingKESEdits, setPendingKESEdits] = useState<Record<string, { statement: string; figures: string[] }>>({})
    const [generatingKES, setGeneratingKES] = useState(false)

    const { data: kesData, isLoading: kesLoading, refetch: refetchKES } = useGetDraftKES(draft?._id, open)

    // Clear stale KES when switching drafts or closing/reopening the dialog
    useEffect(() => {
        setPendingKES([])
        setPendingKESEdits({})
        setLocalKES([])
    }, [open, draft?._id])

    useEffect(() => {
        if (kesData !== undefined) {
            const pending = kesData.filter((k) => k.status === 'pending')
            setPendingKES(pending)
            setPendingKESEdits(
                Object.fromEntries(pending.map((k) => [k._id, { statement: k.statement, figures: k.figures || [] }]))
            )
            // Assign a fresh local `id` to each loaded KES so React can track them
            // by a stable key regardless of DB _id format.
            setLocalKES(
                kesData
                    .filter((k) => k.status === 'approved')
                    .map((k) => ({ id: uuidv4(), _id: k._id, statement: k.statement, figures: k.figures || [] }))
            )
        }
    }, [kesData])

    useEffect(() => {
        if (open && draft) setEdits(initEdits(draft))
    }, [open, draft])

    const set = (field: string) => (e: any) => setEdits((prev: any) => ({ ...prev, [field]: e.target.value }))
    const setChecked = (field: string) => (e: any) => setEdits((prev: any) => ({ ...prev, [field]: e.target.checked }))

    const handleSave = async (overrides?: Partial<typeof edits>) => {
        setSaving(true)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const payload = { ...edits, citation: edits.longCitation || edits.citation, ...overrides }
            const res = await callAPI(
                endpoints.updateDraft.url + draft._id,
                endpoints.updateDraft.method,
                token,
                payload
            )
            if (res?.success) {
                await saveLocalKES(draft._id)
                if (overrides?.status === 'accepted') {
                    invalidateLinkingQueries()
                    if (res.evidenceId && res.data?.s3Name) {
                        processEvidenceForMaia({ evidenceId: res.evidenceId })
                    }
                }
                onSaved(res.data)
                onClose()
            }
        } catch (err) {
            console.error('Failed to save draft:', err)
        } finally {
            setSaving(false)
        }
    }

    const handleAcceptToEvidence = () => handleSave({ status: 'accepted' })

    const handleDelete = async () => {
        setDeleteConfirmOpen(false)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            await callAPI(endpoints.deleteDraft.url + draft._id, endpoints.deleteDraft.method, token)
            onDeleted(draft._id)
            onClose()
        } catch (err) {
            console.error('Failed to delete draft:', err)
        }
    }

    const handlePendingKESTextChange = (id: string, text: string) => {
        setPendingKESEdits((prev) => ({ ...prev, [id]: { ...prev[id], statement: text } }))
    }

    const handlePendingKESFileSelect = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const files = Array.from(e.target.files || []).filter((f) => {
            const ext = f.name.split('.').pop()?.toLowerCase()
            return ext === 'png' || ext === 'jpg' || ext === 'jpeg'
        })
        const readers = files.map(
            (f) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(f)
                    reader.onload = () => resolve(reader.result as string)
                    reader.onerror = reject
                })
        )
        Promise.all(readers).then((base64Array) => {
            setPendingKESEdits((prev) => ({
                ...prev,
                [id]: { ...prev[id], figures: [...(prev[id]?.figures || []), ...base64Array] },
            }))
        })
    }

    const handleApproveKES = async (id: string) => {
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const edit = pendingKESEdits[id]
            await callAPI(endpoints.updateKES.url + id, endpoints.updateKES.method, token, {
                status: 'approved',
                ...(edit?.statement !== undefined && { statement: edit.statement }),
                ...(edit?.figures !== undefined && { figures: edit.figures }),
            })
            const approved = pendingKES.find((k) => k._id === id)
            if (approved) {
                setLocalKES((prev) => [
                    ...prev,
                    {
                        id: uuidv4(),
                        _id: approved._id,
                        statement: edit?.statement ?? approved.statement,
                        figures: edit?.figures ?? approved.figures ?? [],
                    },
                ])
            }
            setPendingKES((prev) => prev.filter((k) => k._id !== id))
        } catch (err) {
            console.error('Failed to approve KES:', err)
        }
    }

    const handleDenyKES = async (id: string) => {
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            await callAPI(endpoints.deleteKES.url + id, endpoints.deleteKES.method, token)
            setPendingKES((prev) => prev.filter((k) => k._id !== id))
        } catch (err) {
            console.error('Failed to deny KES:', err)
        }
    }

    const handleGenerateKES = async () => {
        setGeneratingKES(true)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            await callAPI(endpoints.generateDraftKES.url + draft._id + '/kes', endpoints.generateDraftKES.method, token)
            refetchKES()
        } catch (err) {
            console.error('Failed to generate KES:', err)
        } finally {
            setGeneratingKES(false)
        }
    }

    const handleResetToDraft = async () => {
        setSaving(true)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const res = await callAPI(
                `${endpoints.resetDraftToDraft.url}${draft._id}/reset-to-draft`,
                endpoints.resetDraftToDraft.method,
                token
            )
            if (res?.success) {
                onSaved(res.data)
                onClose()
            }
        } catch (err) {
            console.error('Failed to reset draft:', err)
        } finally {
            setSaving(false)
        }
    }

    const aiEnriched = draft?.category && draft.category !== '---'
    const populations: string[] = store.getState()?.settings?.vmSubpopulations || []
    const isAccepted = draft?.status === 'accepted'

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h3" sx={{ flexGrow: 1 }}>
                            Edit Draft
                        </Typography>
                        {draft?.pmcId && (
                            <Tooltip title="Full-text PDF available via PubMed Central" arrow>
                                <Chip size="small" label="PMC PDF" color="success" sx={{ fontWeight: 'bold' }} />
                            </Tooltip>
                        )}
                        {aiEnriched && (
                            <Tooltip title="Fields were pre-filled by AI from the PDF" arrow>
                                <Chip
                                    size="small"
                                    label="AI Enriched"
                                    icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                    variant="outlined"
                                    color="primary"
                                />
                            </Tooltip>
                        )}
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid
                        container
                        direction="column"
                        spacing={1}
                        sx={{ pointerEvents: isAccepted ? 'none' : 'auto', opacity: isAccepted ? 0.6 : 1 }}
                    >
                        {/* Status badge + actions — always visible */}
                        <Grid item>
                            {/* <Divider sx={{ mb: '24px' }} /> */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '24px',
                                    alignSelf: 'stretch',
                                }}
                            >
                                {(draft?.pubmedId || draft?.originalFileName) && (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                        <Typography variant="18x500" color="figma.grayscale.textHier1">
                                            Source:
                                        </Typography>
                                        <Typography variant="18x400" color="figma.grayscale.textHier2">
                                            {draft?.pubmedId ? 'PubMed' : 'File Upload'}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        pointerEvents: 'auto',
                                        opacity: 1,
                                    }}
                                >
                                    {draft?.originalFileName && (
                                        <Typography variant="18x500" color="figma.grayscale.textHier1">
                                            {draft.originalFileName}
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            padding: '4px 12px',
                                            alignItems: 'center',
                                            gap: '4px',
                                            borderRadius: '30px',
                                            bgcolor:
                                                STATUS_BADGE_COLOR[edits.status as keyof typeof STATUS_BADGE_COLOR] ??
                                                'figma.grayscale.tier4',
                                        }}
                                    >
                                        <Typography variant="14x500" color="figma.grayscale.contentWrapper">
                                            {STATUS_DISPLAY[edits.status as keyof typeof STATUS_DISPLAY] ??
                                                edits.status}
                                        </Typography>
                                    </Box>
                                    {!isAccepted && (
                                        <IconButton size="small" onClick={(e) => setStatusMenuAnchor(e.currentTarget)}>
                                            <ActionsIcon style={{ width: 20, height: 20 }} />
                                        </IconButton>
                                    )}
                                    <Popover
                                        open={Boolean(statusMenuAnchor)}
                                        anchorEl={statusMenuAnchor}
                                        onClose={() => setStatusMenuAnchor(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        PaperProps={{
                                            sx: {
                                                display: 'inline-flex',
                                                padding: '12px',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                borderRadius: '12px',
                                                bgcolor: 'figma.grayscale.tier7',
                                                boxShadow: '0 4px 8px 0 rgba(0,0,0,0.20)',
                                            },
                                        }}
                                    >
                                        {STATUS_ACTIONS.map(({ label, value }) => (
                                            <Box
                                                key={value}
                                                onClick={() => {
                                                    setEdits((prev: any) => ({ ...prev, status: value }))
                                                    setStatusMenuAnchor(null)
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    width: '100%',
                                                    '&:hover': { bgcolor: 'figma.grayscale.tier6' },
                                                }}
                                            >
                                                <Typography variant="14x400" color="figma.grayscale.textHier2">
                                                    {label}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Popover>
                                </Box>
                            </Box>
                            <Divider sx={{ mt: '24px' }} />
                        </Grid>

                        {isAccepted && (
                            <Grid item>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        bgcolor: 'figma.grayscale.tier7',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'figma.grayscale.stroke',
                                    }}
                                >
                                    <Typography variant="14x400" color="figma.grayscale.textHier2">
                                        This draft has been accepted and promoted to evidence. It is read-only.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {/* External/Internal toggle */}
                        <Grid item>
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={edits.internal}
                                            onChange={setChecked('internal')}
                                            name="Internal/External"
                                        />
                                    }
                                    label="External"
                                />
                            </FormControl>
                        </Grid>

                        {/* Title */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Evidence Title"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={edits.name}
                                onChange={set('name')}
                            />
                        </Grid>

                        {/* Unique Study Identifier | PubMed ID */}
                        <Grid container item spacing={1}>
                            <Grid item xs>
                                <TextField
                                    margin="dense"
                                    label="Unique Study Identifier"
                                    type="text"
                                    fullWidth
                                    value={edits.uniqueStudyIdentifier}
                                    onChange={set('uniqueStudyIdentifier')}
                                />
                            </Grid>
                            <Grid item xs>
                                <TextField
                                    margin="dense"
                                    label="PubMed ID"
                                    type="text"
                                    fullWidth
                                    value={edits.pubmedId}
                                    onChange={set('pubmedId')}
                                />
                            </Grid>
                        </Grid>

                        {/* Nickname */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Nickname"
                                type="text"
                                fullWidth
                                value={edits.nickname}
                                onChange={set('nickname')}
                            />
                        </Grid>

                        {/* Key Points */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Key Points"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={edits.abstract}
                                onChange={set('abstract')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        resize: 'vertical',
                                        overflow: 'hidden',
                                        minHeight: '120px',
                                    },
                                    '& textarea': {
                                        resize: 'none',
                                        overflow: 'auto !important',
                                        height: '100% !important',
                                    },
                                }}
                            />
                        </Grid>

                        {/* Citations */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Short Citation"
                                type="text"
                                fullWidth
                                value={edits.shortCitation}
                                onChange={set('shortCitation')}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Long Citation"
                                type="text"
                                fullWidth
                                multiline
                                rows={2}
                                value={edits.longCitation}
                                onChange={set('longCitation')}
                            />
                        </Grid>

                        {/* Author(s) */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="Author(s)"
                                type="text"
                                fullWidth
                                value={edits.author}
                                onChange={set('author')}
                            />
                        </Grid>

                        {/* Population | Publication Type */}
                        <Grid container item spacing={1}>
                            <Grid item xs>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Population</InputLabel>
                                    <Select label="Population" value={edits.population} onChange={set('population')}>
                                        <MenuItem value="---">---</MenuItem>
                                        {populations.map((p: string) => (
                                            <MenuItem key={p} value={p}>
                                                {p}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Publication Type</InputLabel>
                                    <Select
                                        label="Publication Type"
                                        value={edits.publicationType}
                                        onChange={set('publicationType')}
                                    >
                                        <MenuItem value="---">---</MenuItem>
                                        {Object.values(PUBLICATIONS).map((v) => (
                                            <MenuItem key={v} value={v}>
                                                {v}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Year | Category */}
                        <Grid container item spacing={1}>
                            <Grid item xs>
                                <TextField
                                    margin="dense"
                                    label="Year"
                                    type="number"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={edits.year}
                                    onChange={set('year')}
                                />
                            </Grid>
                            <Grid item xs>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Category</InputLabel>
                                    <Select label="Category" value={edits.category} onChange={set('category')}>
                                        <MenuItem value="---">---</MenuItem>
                                        {evidenceCategories.map((c) => (
                                            <MenuItem key={c} value={c}>
                                                {c}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* URL */}
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="URL"
                                type="text"
                                fullWidth
                                placeholder="http://"
                                value={edits.link}
                                onChange={set('link')}
                            />
                        </Grid>

                        {/* Manual KES entry */}
                        <Grid item>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                onClick={handleAddKES}
                            >
                                <AddCircleIcon style={{ width: 20, height: 20 }} />
                                <Typography variant="16x400" color="figma.grayscale.textHier1">
                                    Add Key Evidence Statements
                                </Typography>
                            </Box>
                        </Grid>
                        {localKES.map((kes, index) => (
                            <Grid item key={kes.id}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        padding: '16px',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                        borderRadius: '12px',
                                        border: '1px solid',
                                        borderColor: 'figma.grayscale.stroke',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                                        <Typography
                                            variant="22x300"
                                            color="figma.grayscale.textHier2"
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {index + 1}.
                                        </Typography>
                                        <TextField
                                            variant="standard"
                                            placeholder="Enter here"
                                            value={kes.statement}
                                            onChange={(e) => handleKESTextChange(kes.id, e.target.value)}
                                            multiline
                                            fullWidth
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ flex: '1 0 0' }}
                                        />
                                    </Box>
                                </Box>
                                <IconButton component="label" sx={{ mt: 0.5, borderRadius: 1 }}>
                                    <input
                                        type="file"
                                        multiple
                                        hidden
                                        accept=".png,.jpg,.jpeg"
                                        onChange={(e) => handleKESFileSelect(e, kes.id)}
                                    />
                                    <Typography
                                        variant="16x600"
                                        color="primary.main"
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        Attach Figure
                                        {kes.figures.length > 0 && ` (${kes.figures.length})`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                        ))}

                        {/* AI-Recommended Key Evidence Statements */}
                        <Grid item>
                            <Divider sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    AI-Recommended Key Evidence Statements
                                </Typography>
                            </Box>
                        </Grid>
                        {kesLoading || generatingKES ? (
                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">
                                        {generatingKES ? 'Generating statements…' : 'Loading…'}
                                    </Typography>
                                </Box>
                            </Grid>
                        ) : pendingKES.length === 0 ? (
                            <Grid item>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                    onClick={handleGenerateKES}
                                >
                                    Generate Key Evidence Statements
                                </Button>
                            </Grid>
                        ) : (
                            pendingKES.map((kes, index) => (
                                <Grid item key={kes._id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            padding: '16px',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            borderRadius: '12px',
                                            border: '1px solid',
                                            borderColor: 'figma.grayscale.stroke',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                                            <Typography
                                                variant="22x300"
                                                color="figma.grayscale.textHier2"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {localKES.length + index + 1}.
                                            </Typography>
                                            <TextField
                                                variant="standard"
                                                placeholder="Enter here"
                                                value={pendingKESEdits[kes._id]?.statement ?? kes.statement}
                                                onChange={(e) => handlePendingKESTextChange(kes._id, e.target.value)}
                                                multiline
                                                fullWidth
                                                InputProps={{ disableUnderline: true }}
                                                sx={{ flex: '1 0 0' }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mt: 0.5,
                                        }}
                                    >
                                        <IconButton component="label" sx={{ borderRadius: 1 }}>
                                            <input
                                                type="file"
                                                multiple
                                                hidden
                                                accept=".png,.jpg,.jpeg"
                                                onChange={(e) => handlePendingKESFileSelect(e, kes._id)}
                                            />
                                            <Typography
                                                variant="16x600"
                                                color="primary.main"
                                                sx={{ textTransform: 'uppercase' }}
                                            >
                                                Attach Figure
                                                {(pendingKESEdits[kes._id]?.figures?.length ?? 0) > 0 &&
                                                    ` (${pendingKESEdits[kes._id].figures.length})`}
                                            </Typography>
                                        </IconButton>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Deny" arrow>
                                                <IconButton onClick={() => handleDenyKES(kes._id)} size="small">
                                                    <SvgIcon
                                                        component={DenyIcon}
                                                        inheritViewBox
                                                        sx={{ color: 'figma.grayscale.textHier2', fontSize: 22 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Approve" arrow>
                                                <IconButton onClick={() => handleApproveKES(kes._id)} size="small">
                                                    <SvgIcon
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                        sx={{ color: 'primary.main', fontSize: 22 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteConfirmOpen(true)}
                        disabled={saving}
                    >
                        Delete Draft
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {isAccepted && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleResetToDraft}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} /> : null}
                            >
                                {saving ? 'Resetting…' : 'Reset to Draft'}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleSave()}
                            disabled={saving || isAccepted}
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAcceptToEvidence}
                            disabled={saving || isAccepted}
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? 'Saving…' : 'Accept to Evidence'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete this draft?</DialogTitle>
                <DialogContent>
                    <Typography>"{draft?.name}" will be permanently deleted and cannot be recovered.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
